import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * One visible action per row; the rest under "More". Three buttons on every
 * row of a table is what makes a register look like a control panel.
 *
 * The menu is positioned against the viewport, not the row: tables sit in a
 * horizontally scrolling wrapper, and a menu positioned inside it is clipped
 * and grows the wrapper a scrollbar.
 */
export interface RowAction { label: string; onClick: () => void; danger?: boolean; disabled?: boolean }

export function RowActions({ primary, more }: { primary?: ReactNode; more: RowAction[] }) {
  const ref = useRef<HTMLDetailsElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  const place = () => {
    const summary = ref.current?.querySelector('summary');
    if (!summary) return;
    const r = summary.getBoundingClientRect();
    setPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
  };

  useEffect(() => {
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) ref.current.open = false; };
    const closeAll = () => { if (ref.current) ref.current.open = false; };
    document.addEventListener('click', close);
    window.addEventListener('scroll', closeAll, true);
    window.addEventListener('resize', closeAll);
    return () => {
      document.removeEventListener('click', close);
      window.removeEventListener('scroll', closeAll, true);
      window.removeEventListener('resize', closeAll);
    };
  }, []);

  const items = more.filter(Boolean);
  return (
    <span className="row-actions">
      {primary}
      {items.length > 0 && (
        <details className="row-more" ref={ref} onToggle={(e) => { if ((e.currentTarget as HTMLDetailsElement).open) place(); else setPos(null); }}>
          <summary aria-label="More actions">More</summary>
          <div className="row-more-menu" style={pos ? { top: pos.top, right: pos.right } : undefined}>
            {items.map((a) => (
              <button key={a.label} className={a.danger ? 'danger' : ''} disabled={a.disabled}
                onClick={() => { if (ref.current) ref.current.open = false; a.onClick(); }}>
                {a.label}
              </button>
            ))}
          </div>
        </details>
      )}
    </span>
  );
}
